import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../atoms/button";
import ToastContext from "../../context/toastProvider";
import RegisterModal from "./registerModal";
import { getAllUsers, searchUserIds } from "./functions";
import { USER_COLUMNS } from "./usersColumns";
import { applyRolePreset, matchRolePreset, ROLE_PRESETS } from "./rolePresets";
import SearchInput from "../filters/SearchInput";
import FilterChips from "../filters/FilterChips";
import ActiveFiltersBar from "../filters/ActiveFiltersBar";
import { buildActiveFilterItems } from "../filters/columnFilterState";
import { useUrlColumnFilters } from "../filters/useUrlColumnFilters";
import DataTable from "../table/DataTable";
import { useDataTable } from "../table/useDataTable";

const FILTER_SCHEMA = Object.fromEntries(
    USER_COLUMNS.filter((column) => column.filter && column.filter !== "ids").map((column) => [column.key, column.filter])
);
const NO_DEFAULT_FILTERS = () => [];

const Users = ({ user }) => {
    const navigate = useNavigate();
    const { state } = useLocation() || {};
    const { badToast } = useContext(ToastContext);
    const { filters, q, setFilters, setSearch, clearAll } = useUrlColumnFilters({ schema: FILTER_SCHEMA, getDefaultFilters: NO_DEFAULT_FILTERS });

    const [users, setUsers] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [register, setRegister] = useState(false);
    const [qInput, setQInput] = useState(q);
    const [searchIds, setSearchIds] = useState(null);
    const [searchFailed, setSearchFailed] = useState(false);
    const badToastRef = useRef(badToast);
    badToastRef.current = badToast;

    const loadUsers = async () => {
        setLoading(true);
        const data = await getAllUsers(user.token);
        setLoading(false);
        if (data.type === "success") {
            setUsers(data.data ?? []);
            setLoadError(null);
        } else {
            setLoadError(data.message || "Erro ao buscar usuários!");
            badToast(data.message);
        }
    };

    useEffect(() => {
        loadUsers();
        // Busca rápida de cliente vinda da Home (presetName/presetEmail).
        const preset = state?.presetName ?? state?.presetEmail;
        if (preset && !q) setSearch(preset);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setQInput(q);
    }, [q]);

    useEffect(() => {
        if (!q) {
            setSearchIds(null);
            setSearchFailed(false);
            return undefined;
        }
        let cancelled = false;
        setSearchIds(null);
        setSearchFailed(false);
        searchUserIds(user.token, q).then((ids) => {
            if (cancelled) return;
            if (ids === null) {
                // Falha não pode parecer "nada encontrado": avisa e não aplica o filtro por ids.
                setSearchFailed(true);
                badToastRef.current("Erro ao buscar usuários!");
            } else {
                setSearchIds(ids);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [q, user.token]);

    const columnFilters = useMemo(
        () => (q && searchIds ? [...filters, { id: "_id", value: { term: q, ids: searchIds } }] : filters),
        [filters, q, searchIds]
    );
    const columnFiltersRef = useRef(columnFilters);
    columnFiltersRef.current = columnFilters;
    const onColumnFiltersChange = useCallback(
        (updater) => setFilters(typeof updater === "function" ? updater(columnFiltersRef.current) : updater),
        [setFilters]
    );

    const { table, resetLayout } = useDataTable({ tableKey: "admin-users", columns: USER_COLUMNS, data: users, columnFilters, onColumnFiltersChange });

    const clearSearch = () => {
        setQInput("");
        setSearch("");
    };
    const submitSearch = () => {
        if (!qInput.trim()) {
            clearSearch();
            return;
        }
        setSearch(qInput);
    };
    const activeItems = buildActiveFilterItems(table, { exclude: ["_id"] });
    if (q) activeItems.unshift({ id: "q", label: `Busca: "${q}"`, onRemove: clearSearch });
    const resultCount = table.getFilteredRowModel().rows.length;
    const searching = Boolean(q) && searchIds === null && !searchFailed;

    const resultText = !loading && !searching && !searchFailed
        ? (resultCount === 1 ? "1 usuário encontrado" : `${resultCount.toLocaleString("pt-BR")} usuários encontrados`)
        : null;

    return (
        // --table-offset: altura do que fica acima e abaixo da tabela (linhas 1 e 2, toolbar e paginação).
        <main className="text-left" style={{ "--table-offset": "150px" }}>
            <div className="flex flex-col gap-1 border-b border-primary/60 px-2 py-1">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-base font-semibold text-laranjinha">Usuários</h1>
                    <SearchInput
                        value={qInput}
                        onChange={setQInput}
                        onSearch={submitSearch}
                        onClear={clearSearch}
                        placeholder="Buscar por nome, e-mail, CPF ou telefone…"
                    />
                    <Button classes="bg-blue-300 hover:bg-blue-400 inline whitespace-nowrap text-sm text-black" click={() => setRegister(true)}>Cadastrar usuário</Button>
                </div>
                <FilterChips
                    ariaLabel="Filtros rápidos de perfil"
                    options={ROLE_PRESETS}
                    selectedKey={matchRolePreset(filters)}
                    onSelect={(preset) => setFilters((current) => applyRolePreset(current, preset))}
                />
            </div>

            {loadError && !users.length ? (
                <p className="m-8 text-center text-red-600">{loadError}</p>
            ) : (
                <DataTable
                    table={table}
                    resetLayout={resetLayout}
                    loading={loading || searching}
                    exportable={user.roles.includes("master")}
                    exportFileName="usuarios"
                    toolbarStart={
                        <>
                            <ActiveFiltersBar items={activeItems} onClearAll={() => { setQInput(""); clearAll(); }} />
                            {resultText ? <span className="whitespace-nowrap text-xs text-laranjinha/50">{resultText}</span> : null}
                        </>
                    }
                />
            )}
            {register ? <RegisterModal handleClose={() => setRegister(false)} openUser={(userId) => navigate(`/manafix/users/${userId}`)} /> : null}
        </main>
    );
};

export default Users;
